import type {
  MessageHandlerResult,
  MessageStats,
} from '@/services/abstract/poc/MessagingService';
import type { HeliaNode } from '@/types/poc/helia';
import type {
  GroupStatus,
  LibP2pWithServices,
  TurnConfig,
} from '@/types/poc/libp2p';
import type { P2PDiagnostics } from '@/types/P2PDiagnostics';

export interface SharedNodeOptions {
  bootstrapNodes: string[];
  userId?: string;
  turnConfig?: TurnConfig;
  onMessage: MessageCallback;
}

export interface SixSubscriptionOptions {
  sixId: string;
  onConnectionUpdate: ConnectionUpdateCallback;
}

export interface SharedNodeStartResult {
  peerId: string;
  isSuccessful: boolean;
}

export type MessageCallback = (
  result: MessageHandlerResult,
  stats: MessageStats
) => void;

export type ConnectionUpdateCallback = () => void;

export interface NodeMetadata {
  status: GroupStatus;
  config: {
    relayAddresses: string[];
  };
}

export abstract class P2PStackManager {
  abstract startSharedNode(options: SharedNodeOptions): Promise<SharedNodeStartResult>;
  abstract subscribeToSix(options: SixSubscriptionOptions): void;
  abstract shutdownNode(): Promise<void>;
  abstract getLibp2pNode(): LibP2pWithServices | null;
  abstract getHeliaNode(): HeliaNode | null;
  abstract getConnectionInfo(): Array<{
    peerId: string;
    address: string;
    isDirect: boolean;
    isRelay: boolean;
    direction: 'inbound' | 'outbound';
  }>;
  abstract reportBitswapStall(sixId: string): void;
  abstract ensureBootstrapNodesDialed(bootstrapNodes: string[]): void;
  /** Read-only diagnostics snapshot for the debug panel. Additive and side-effect-free;
   * never mutates P2P/Helia state. `brokerMultiaddr` is the configured broker address
   * (from the organization slice, passed in by the thunk) used to derive broker status. */
  abstract getDiagnosticsSnapshot(brokerMultiaddr: string | null): Promise<P2PDiagnostics>;
}
